// RenameFileDialog — one act, one field (ADR-038: a dialog for one decision):
// the document's display name, on the DialogForm container (every open re-seeds
// from the file as it is then; the discard guard rides the shell). The edit
// rides the row stamp (ADR-084): the save carries the file's stamp as the dialog
// opened, the dialog awaits it — a refusal stays here as the line (a stale save
// offers Load the latest, the file re-read), never a toast over a closed dialog.

import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import type { UpdateFileRequest } from '@ems-app/schemas/files/requests';
import { DialogForm } from './forms/DialogForm.js';
import { readLatest } from './forms/readLatest.js';
import { useFormShell } from './forms/useFormShell.js';
import { Input } from '../ui/Input.js';
import { Label } from '../ui/Label.js';
import { displayName, shortFileName, type LibraryFile } from './filesDisplay.js';
import { fetchFile, filesKeys } from '../../hooks/useFiles.js';

const Schema = z.object({ name: z.string().trim().min(1).max(255) });
type Values = z.infer<typeof Schema>;
/** What the seed reads — the view's file and the wire's fresh read both carry it. */
type RenameRow = Pick<LibraryFile, 'display_name' | 'original_name' | 'row_version'>;

export interface RenameFileDialogProps {
  open: boolean;
  projectId: string;
  file: LibraryFile;
  onClose: () => void;
  /** The save, awaited — a rejection is the dialog's line, so the host never catches it (a success toast is its own). */
  onRename: (file: LibraryFile, body: UpdateFileRequest) => Promise<unknown> | void;
}

export function RenameFileDialog({ open, projectId, file, onClose, onRename }: RenameFileDialogProps) {
  const { t } = useTranslation('files');
  const qc = useQueryClient();
  const shell = useFormShell<Values, RenameRow>({
    schema: Schema as unknown as z.ZodType<Values, Values>,
    row: file,
    values: (r) => ({ name: r ? displayName(r) : '' }),
    // The fresh file: a quiet read (a file gone meanwhile is said in the dialog), into the surface's entry once it answers.
    reload: async () => {
      const fresh = await readLatest(qc, filesKeys.detail(projectId, file.id), () => fetchFile(projectId, file.id, true));
      // The rows behind — a shelf's, a flow's — show it too (the dialog opens from a row's menu with no surface open).
      void qc.invalidateQueries({ queryKey: filesKeys.all(projectId) });
      return fresh;
    },
    onSubmit: async (v, { stamp }) => {
      await onRename(file, { row_version: stamp, display_name: v.name });
    },
    onSuccess: onClose,
  });
  return (
    <DialogForm shell={shell} open={open} onClose={onClose} title={t('rename.title')} submitLabel={t('rename.submit')} maxWidth="max-w-md">
      <Controller
        control={shell.form.control}
        name="name"
        render={({ field, fieldState }) => (
          <div>
            <Label htmlFor="rename-file-name">{t('rename.name')}</Label>
            <Input id="rename-file-name" value={field.value} onChange={field.onChange} aria-invalid={fieldState.error ? true : undefined} />
            <p className="text-xs text-[var(--color-text-secondary)]">{t('rename.fileNameStays', { name: shortFileName(file.original_name) })}</p>
          </div>
        )}
      />
    </DialogForm>
  );
}
